import React, { useEffect, useReducer, useState } from 'react'

import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { openChat } from '../../../util/set-crisp-session-info'
import { useAuthPageRecording } from '../../../hooks/use-auth-page-recording'
import SignupBannerView from './components/signup-banner'
import SignupLayout from './components/signup-layout'
import SignupModals from './components/signup-modals'
import ConnectStep from './components/connect-step'
import { useEmailSignup } from './hooks/use-email-signup'
import { usePlatformSignup } from './hooks/use-platform-signup'
import { useSignupBootstrap } from './hooks/use-signup-bootstrap'
import { useSignupModals } from './hooks/use-signup-modals'
import { useSignupNavigation } from './hooks/use-signup-navigation'
import { useSignupSession } from './hooks/use-signup-session'
import { useSignupAccessRequest } from './hooks/use-signup-access-request'
import { parseSignupQueryParams } from './query-params'
import { initialState, reducer } from './reducer'
import { isConnectPlatform } from './outcomes'
import { trackSignup } from './analytics'
import { INewSignupProps } from './types'

const NewSignup = ({ location }: INewSignupProps): React.ReactElement => {
  // Derived from the URL while rendering. Copying these into state from an effect is what made
  // the old page redirect to the wrong place on its first run.
  const queryParams = parseSignupQueryParams(location.search)

  // Lazy initial state, not an effect: the sign-in page stashes the address it could not find,
  // so the field is right on the first paint.
  const [initialEmail] = useState(() => sessionStorage.getItem('vtUserEmail') || '')
  const [state, localDispatch] = useReducer(reducer, initialState)
  const [userEmail, setUserEmail] = useState(initialEmail)

  const modals = useSignupModals()
  const navigation = useSignupNavigation()
  const session = useSignupSession()
  const platformSignup = usePlatformSignup({ localDispatch, navigation, session })
  const emailSignup = useEmailSignup({ localDispatch, modals, navigation })
  const accessRequest = useSignupAccessRequest({ localDispatch, readProviderToken: platformSignup.readProviderToken })

  const isLeavingForApp = useSignupBootstrap({ queryParams, search: location.search, initialEmail })
  useAuthPageRecording('signup')

  // Reported from the one place that knows what actually reached the screen. Reporting it at
  // each dispatch site meant recomputing the banner beside every one of them, and a new kind
  // would have rendered correctly while going unreported.
  useEffect(() => {
    if (state.banner.kind !== 'none') {
      trackSignup('SIGNUP_BANNER_SHOWN', { kind: state.banner.kind })
    }
  }, [state.banner])

  // The modals offer whichever platform the backend named, which is not necessarily one this
  // page can start: an email-platform company has no button to press.
  const connectWithPlatform = (platform: PlatformEnum, loginHint?: string) => {
    if (!isConnectPlatform(platform)) return

    trackSignup('ORG_EXISTS_ON_ANOTHER_PLATFORM_CHOOSE_EXISTING', { platform })
    platformSignup.signupWith(platform, loginHint)
  }

  // A live session has nothing to sign up for. The redirect runs from an effect, which is after
  // paint, so without this the form is drawn for a frame before it is navigated away.
  if (isLeavingForApp) {
    return <></>
  }

  return (
    <>
      <SignupLayout
        isBusy={state.busy !== null}
        banner={
          <SignupBannerView
            banner={state.banner}
            onLogin={() => navigation.goToLogin()}
            onResetPassword={() => navigation.goToLogin('forgotpassword')}
            // An email-platform organisation has no provider to start, so its sign-in is the password page.
            onLoginWithPlatform={(platform) => (isConnectPlatform(platform) ? connectWithPlatform(platform) : navigation.goToLogin())}
            onRequestAccess={(params) => void accessRequest.requestAccess(params)}
            isRequestingAccess={accessRequest.isRequestingAccess}
            onContactSupport={() => openChat()}
          />
        }
      >
        <ConnectStep
          email={initialEmail}
          busy={state.busy}
          focusedPlatform={queryParams.focusedPlatform ?? null}
          onSubmitEmail={(email) => {
            setUserEmail(email)
            emailSignup.submitEmail(email)
          }}
          onEmailChanged={() => {
            // useReducer has no eager bailout, so an unconditional dispatch re-runs the whole
            // container on every keystroke to produce the state it already had.
            if (state.banner.kind !== 'none') {
              localDispatch({ type: 'bannerCleared' })
            }
          }}
          onPlatformSignup={platformSignup.signupWith}
          onShowAllOptions={() => {
            trackSignup('SIGNUP_DIFFERENT_METHOD_CLICKED', { platform: queryParams.focusedPlatform })
            navigation.goToAllSignupOptions()
          }}
          onLogin={() => navigation.goToLogin()}
        />
      </SignupLayout>

      <SignupModals
        modals={modals}
        // The modal answers an address the visitor typed, so the provider is asked for that account.
        onConnectWithPlatform={(platform) => connectWithPlatform(platform, userEmail)}
        onSignupWithEmail={() => {
          modals.hideSignupWithAnotherPlatform()
          navigation.goToPersonalInfo(userEmail, true)
        }}
        onSigninWithPlatform={(platform) => {
          modals.hideSignupWithAnotherPlatform()
          navigation.goToLoginWithPlatform(platform)
        }}
      />
    </>
  )
}

export default NewSignup
